/**
 * LuckyDuckyReportsAdminTab
 * Admin → Lucky Ducky → Reports.
 * Backed by /api/admin/lucky-ducky/reports/*.
 * Shows authoritative spin-audit telemetry: handle, hold, RTP observed,
 * feature/respin frequency per math profile. Test-bot activity excluded
 * by default; toggle to include for QA.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WINDOWS = [
    { label: '1 hr',   hours: 1 },
    { label: '24 hrs', hours: 24 },
    { label: '7 days', hours: 24 * 7 },
    { label: '30 days',hours: 24 * 30 },
];

const money = (n) => {
    const v = Number(n || 0);
    const s = v < 0 ? '-' : '';
    return `${s}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const pct = (n) => `${Number(n || 0).toFixed(3)}%`;
const int = (n) => Number(n || 0).toLocaleString();

export default function LuckyDuckyReportsAdminTab() {
    const [hours, setHours] = useState(24);
    const [includeBots, setIncludeBots] = useState(false);
    const [profileFilter, setProfileFilter] = useState('');
    const [summary, setSummary] = useState(null);
    const [recent, setRecent] = useState([]);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [spinLookup, setSpinLookup] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams({ hours: String(hours), include_bots: String(includeBots) });
            if (profileFilter) q.set('profile_id', profileFilter);
            const [s, r] = await Promise.all([
                axios.get(`${API}/admin/lucky-ducky/reports/summary?${q}`, { withCredentials: true }),
                axios.get(`${API}/admin/lucky-ducky/reports/recent-spins?${q}&limit=50`, { withCredentials: true }),
            ]);
            setSummary(s.data);
            setRecent(r.data.spins || []);
        } catch (e) {
            toast.error('Failed to load Lucky Ducky reports', { description: e?.response?.data?.detail || e.message });
        } finally {
            setLoading(false);
        }
    }, [hours, includeBots, profileFilter]);

    useEffect(() => { load(); }, [load]);

    const openSpin = async (spinId) => {
        if (!spinId) return;
        setDetailLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/lucky-ducky/reports/spin/${spinId}`, { withCredentials: true });
            setDetail(data);
        } catch (e) {
            toast.error('Spin not found', { description: e?.response?.data?.detail || e.message });
        } finally {
            setDetailLoading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="lucky-ducky-reports">
            <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-xl font-semibold text-white">Lucky Ducky · Reports</h2>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                    {WINDOWS.map(w => (
                        <button
                            key={w.hours}
                            data-testid={`ld-reports-window-${w.hours}`}
                            onClick={() => setHours(w.hours)}
                            className={`px-3 py-1 text-sm rounded-md border ${hours === w.hours
                                ? 'bg-amber-500 border-amber-400 text-black font-semibold'
                                : 'bg-slate-800/60 border-slate-700 text-slate-200 hover:bg-slate-700'}`}>
                            {w.label}
                        </button>
                    ))}
                    <label className="flex items-center gap-2 text-sm text-slate-200 ml-3">
                        <input
                            data-testid="ld-reports-include-bots"
                            type="checkbox"
                            checked={includeBots}
                            onChange={(e) => setIncludeBots(e.target.checked)} />
                        Include TEST BOT spins
                    </label>
                    <input
                        data-testid="ld-reports-profile-filter"
                        placeholder="Filter by profile_id"
                        value={profileFilter}
                        onChange={(e) => setProfileFilter(e.target.value)}
                        className="px-3 py-1 text-sm rounded-md bg-slate-800/60 border border-slate-700 text-slate-200 w-56" />
                    <button
                        data-testid="ld-reports-refresh"
                        onClick={load}
                        disabled={loading}
                        className="p-2 rounded-md bg-slate-800/60 border border-slate-700 text-slate-200 hover:bg-slate-700 disabled:opacity-40">
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>
            </div>

            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <StatCard label="Spins"   value={int(summary.totals.spins)} testid="ld-total-spins" />
                    <StatCard label="Handle"  value={money(summary.totals.handle_dollars)} testid="ld-total-handle" />
                    <StatCard label="Payout"  value={money(summary.totals.payout_dollars)} testid="ld-total-payout" />
                    <StatCard label="Hold"    value={money(summary.totals.hold_dollars)}
                              tone={summary.totals.hold_dollars >= 0 ? 'positive' : 'negative'} testid="ld-total-hold" />
                    <StatCard label="Observed RTP" value={pct(summary.totals.observed_rtp_pct)} testid="ld-total-rtp" />
                </div>
            )}

            <section className="rounded-lg border border-slate-700/60 bg-slate-900/40">
                <header className="px-4 py-3 border-b border-slate-700/60 text-slate-200 font-medium">
                    Per-Profile Breakdown
                </header>
                <table className="w-full text-sm text-slate-200" data-testid="ld-per-profile-table">
                    <thead className="text-slate-400 text-left">
                        <tr>
                            <th className="px-4 py-2">Profile</th>
                            <th className="px-4 py-2 text-right">Spins</th>
                            <th className="px-4 py-2 text-right">Handle</th>
                            <th className="px-4 py-2 text-right">Payout</th>
                            <th className="px-4 py-2 text-right">Hold</th>
                            <th className="px-4 py-2 text-right">Obs. RTP</th>
                            <th className="px-4 py-2 text-right">Feat. Freq</th>
                            <th className="px-4 py-2 text-right">1 in</th>
                            <th className="px-4 py-2 text-right">Big Win</th>
                            <th className="px-4 py-2 text-right">Players</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(summary?.per_profile || []).length === 0 && (
                            <tr><td colSpan={10} className="px-4 py-6 text-center text-slate-500">
                                No spins in this window.
                            </td></tr>
                        )}
                        {(summary?.per_profile || []).map(r => (
                            <tr key={r.profile_id} className="border-t border-slate-800/60 hover:bg-slate-800/40">
                                <td className="px-4 py-2 font-mono">{r.profile_id}</td>
                                <td className="px-4 py-2 text-right">{int(r.spins)}</td>
                                <td className="px-4 py-2 text-right">{money(r.handle_dollars)}</td>
                                <td className="px-4 py-2 text-right">{money(r.payout_dollars)}</td>
                                <td className="px-4 py-2 text-right">{money(r.hold_dollars)}</td>
                                <td className="px-4 py-2 text-right">{pct(r.observed_rtp_pct)}</td>
                                <td className="px-4 py-2 text-right">{pct(r.feature_freq_pct)}</td>
                                <td className="px-4 py-2 text-right">{r.feature_entry_1_in ?? '—'}</td>
                                <td className="px-4 py-2 text-right">{money((r.biggest_win_credits || 0) / 100)}</td>
                                <td className="px-4 py-2 text-right">{int(r.unique_players)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <section className="rounded-lg border border-slate-700/60 bg-slate-900/40">
                <header className="px-4 py-3 border-b border-slate-700/60 flex items-center gap-3">
                    <span className="text-slate-200 font-medium">Recent Spins (audit)</span>
                    <span className="text-xs text-slate-500">click a row to inspect the ledger trail</span>
                    <div className="ml-auto flex items-center gap-2">
                        <input
                            data-testid="ld-spin-lookup-input"
                            placeholder="spin_id…"
                            value={spinLookup}
                            onChange={(e) => setSpinLookup(e.target.value.trim())}
                            className="px-2 py-1 text-xs rounded bg-slate-800/60 border border-slate-700 text-slate-200 w-56" />
                        <button
                            data-testid="ld-spin-lookup-btn"
                            onClick={() => openSpin(spinLookup)}
                            className="p-1.5 rounded bg-slate-800/60 border border-slate-700 hover:bg-slate-700">
                            <Search size={14} />
                        </button>
                    </div>
                </header>
                <div className="max-h-96 overflow-auto">
                    <table className="w-full text-xs text-slate-200" data-testid="ld-recent-spins-table">
                        <thead className="text-slate-400 text-left sticky top-0 bg-slate-900/95">
                            <tr>
                                <th className="px-3 py-2">At (UTC)</th>
                                <th className="px-3 py-2">Spin ID</th>
                                <th className="px-3 py-2">Profile</th>
                                <th className="px-3 py-2">User</th>
                                <th className="px-3 py-2 text-right">Wager</th>
                                <th className="px-3 py-2 text-right">Win</th>
                                <th className="px-3 py-2">Path</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent.map(s => (
                                <tr key={s.spin_id}
                                    className="border-t border-slate-800/60 hover:bg-slate-800/40 cursor-pointer"
                                    onClick={() => openSpin(s.spin_id)}
                                    data-testid={`ld-spin-row-${s.spin_id}`}>
                                    <td className="px-3 py-1.5 whitespace-nowrap">{s.created_at?.slice(0, 19)}</td>
                                    <td className="px-3 py-1.5 font-mono">{s.spin_id?.slice(0, 8)}</td>
                                    <td className="px-3 py-1.5 font-mono">{s.profile_id}</td>
                                    <td className="px-3 py-1.5 font-mono">
                                        {s.test_bot ? <span className="px-1.5 py-0.5 rounded bg-amber-900/60 text-amber-200">TEST</span> : ''}
                                        {' '}{s.user_id?.slice(-6)}
                                    </td>
                                    <td className="px-3 py-1.5 text-right">{money(s.wager_dollars)}</td>
                                    <td className={`px-3 py-1.5 text-right ${s.total_win_dollars > 0 ? 'text-green-400' : 'text-slate-400'}`}>
                                        {money(s.total_win_dollars)}
                                    </td>
                                    <td className="px-3 py-1.5">
                                        {s.feature ? <span className="px-1.5 py-0.5 rounded bg-purple-900/60 text-purple-200">FEATURE</span> :
                                         s.respin ? <span className="px-1.5 py-0.5 rounded bg-blue-900/60 text-blue-200">RESPIN</span> :
                                         <span className="text-slate-500">base</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {detail && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
                     onClick={() => setDetail(null)}
                     data-testid="ld-spin-detail-modal">
                    <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-3xl w-full max-h-[85vh] overflow-auto p-6"
                         onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-white mb-3">Spin Audit: {detail.spin_id}</h3>
                        {detailLoading && <div className="text-slate-400">Loading…</div>}
                        <div className="grid grid-cols-2 gap-2 text-sm text-slate-200">
                            <div><span className="text-slate-500">Profile:</span> {detail.profile_id}</div>
                            <div><span className="text-slate-500">Profile hash:</span> <span className="font-mono text-xs">{detail.profile_hash?.slice(0, 24)}…</span></div>
                            <div><span className="text-slate-500">User:</span> <span className="font-mono">{detail.user_id}</span></div>
                            <div><span className="text-slate-500">Client req:</span> <span className="font-mono text-xs">{detail.client_request_id}</span></div>
                            <div><span className="text-slate-500">Wager:</span> {detail.wager_key} · {money(detail.wager_dollars)}</div>
                            <div><span className="text-slate-500">Win:</span> {money(detail.total_win_dollars)} ({detail.total_win_credits} credits)</div>
                            <div><span className="text-slate-500">Balance before:</span> {money(detail.balance_before_dollars)}</div>
                            <div><span className="text-slate-500">Balance after:</span> {money(detail.balance_after_dollars)}</div>
                            <div><span className="text-slate-500">Base bonus count:</span> {detail.base?.bonus_count}</div>
                            <div><span className="text-slate-500">Respin?:</span> {detail.respin ? 'yes' : 'no'}</div>
                            <div><span className="text-slate-500">Feature?:</span> {detail.feature ? `${detail.feature.free_plays_executed} plays, ${detail.feature.retriggers} retriggers, win ${money((detail.feature.total_win || 0) / 100)}` : 'no'}</div>
                        </div>
                        <h4 className="text-white mt-4 mb-2">Ledger trail</h4>
                        <table className="w-full text-xs text-slate-200">
                            <thead className="text-slate-400 text-left">
                                <tr><th>At</th><th>Type</th><th>Amount</th><th>Balance after</th></tr>
                            </thead>
                            <tbody>
                                {(detail.ledger || []).map(t => (
                                    <tr key={t._id} className="border-t border-slate-800/60">
                                        <td className="py-1">{new Date(t.created_at).toISOString().slice(0, 19)}</td>
                                        <td className="py-1">{t.transaction_type}</td>
                                        <td className={`py-1 text-right ${t.amount >= 0 ? 'text-green-400' : 'text-red-300'}`}>{money(t.amount)}</td>
                                        <td className="py-1 text-right">{money(t.balance_after)}</td>
                                    </tr>
                                ))}
                                {(detail.ledger || []).length === 0 && (
                                    <tr><td colSpan={4} className="text-center py-3 text-slate-500">No ledger rows.</td></tr>
                                )}
                            </tbody>
                        </table>
                        <button data-testid="ld-spin-detail-close" onClick={() => setDetail(null)}
                                className="mt-4 px-4 py-2 rounded bg-slate-700 hover:bg-slate-600 text-white">Close</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, tone, testid }) {
    const toneClass = tone === 'positive' ? 'text-green-400' :
                      tone === 'negative' ? 'text-red-300' : 'text-white';
    return (
        <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-3" data-testid={testid}>
            <div className="text-xs text-slate-400 uppercase tracking-wide">{label}</div>
            <div className={`text-xl font-semibold ${toneClass}`}>{value}</div>
        </div>
    );
}
